import { ManagerBase } from '@core/ManagerBase';
import type { EventBus } from '@core/events/EventBus';
import type { IGameManager } from '@core/interfaces';
import type { BanknoteInstance, RoundState } from './banknotes/models/BanknoteTypes';
import {
  BanknoteCatalogService,
  JsonBanknoteCatalogRepository,
  RoundBanknoteGenerator,
} from './banknotes';
import { RoundController } from './RoundController';
import { GameSession } from './GameSession';
import {
  GameplayEvents,
  type RoundCorrectEvent,
  type RunEndedEvent,
} from './GameplayEvents';

/**
 * Oyun oturumu ve tur yaşam döngüsü — UI'dan bağımsız.
 */
export class GameManager extends ManagerBase implements IGameManager {
  private readonly session = new GameSession();
  private readonly catalogService = new BanknoteCatalogService(new JsonBanknoteCatalogRepository());
  private readonly round: RoundController;

  constructor(private readonly eventBus: EventBus) {
    super();
    this.catalogService.initialize();
    this.round = new RoundController(new RoundBanknoteGenerator(this.catalogService));
  }

  get score(): number {
    return this.session.currentScore;
  }

  get isRunActive(): boolean {
    return this.session.isActive;
  }

  getRoundState(): RoundState {
    return this.round.getState();
  }

  getTargetBanknote(): BanknoteInstance | null {
    return this.round.getState().target;
  }

  getGridBanknotes(): readonly BanknoteInstance[] {
    return this.round.getState().grid;
  }

  startRun(): void {
    this.session.start();
    this.round.reset();
    this.eventBus.publish({ type: GameplayEvents.runStarted });
    this.beginRound();
  }

  beginRound(): void {
    this.round.beginRound();
    this.eventBus.publish({ type: GameplayEvents.roundPreview });
  }

  completePreview(): void {
    this.round.enterGridPhase();
    this.eventBus.publish({ type: GameplayEvents.roundGrid });
  }

  selectBanknote(instanceId: string): 'correct' | 'wrong' | 'ignored' {
    if (!this.session.isActive) {
      return 'ignored';
    }

    const isCorrect = this.round.evaluateSelection(instanceId);

    if (isCorrect) {
      this.session.incrementScore();
      const event: RoundCorrectEvent = {
        type: GameplayEvents.roundCorrect,
        score: this.session.currentScore,
      };
      this.eventBus.publish(event);
      return 'correct';
    }

    const finalScore = this.session.end();
    const endEvent: RunEndedEvent = {
      type: GameplayEvents.runEnded,
      finalScore,
    };
    this.eventBus.publish(endEvent);
    return 'wrong';
  }

  prepareNextRound(): void {
    this.round.reset();
    this.beginRound();
  }

  protected onInitialize(): void {
    // Banknot kataloğu constructor'da yüklenir.
  }

  protected onShutdown(): void {
    this.session.reset();
    this.round.reset();
  }
}
